import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const URL_PARAMS = {
  initials: 'initials',
  category: 'category',
  filter: 'filter',
  page: 'page',
};

const CATEGORY_INPUT_NAME = 'bloopers-category';

const DRAWER_FILTERS_WRAPPER_SELECTOR = '#filters-drawer .facets-drawer__filters .facets__filters-wrapper';

const HEADER_SEARCH_FORM_SELECTOR = '.bloopers-collection-header [data-bloopers-plp-form]';

const CATEGORY_FILTER_NAV_SELECTOR = '.bloopers-collection-header .blooper-filters';

const SEARCH_ENDPOINT = '/apps/search/monogram-bloopers';

const DEFAULT_PAGE_SIZE = 36;

/**
 * @typedef {object} BloopersPlpRefs
 * @property {HTMLElement} results
 * @property {HTMLElement} status
 * @property {HTMLUListElement} grid
 * @property {HTMLElement} pagination
 * @property {HTMLTemplateElement} checkmarkSvg
 * @property {HTMLButtonElement} notifyMeButton
 */

/**
 * Shape of a product returned by the `/apps/search/monogram-bloopers` endpoint.
 * @typedef {object} BlooperProduct
 * @property {string} [title]
 * @property {string} [type]
 * @property {string} [blooperType]
 * @property {string} [handle]
 * @property {string} [imageUrl]
 * @property {string} [blooperValue]
 * @property {string} [size]
 * @property {string | number} [price]
 * @property {string | number} [variantId]
 */

/**
 * @typedef {object} BlooperPagination
 * @property {number} [page]
 * @property {number} [limit]
 * @property {number} [totalPages]
 */

/**
 * Custom element powering the Bloopers PLP initials search, category filtering, and result grid.
 * @extends {Component<BloopersPlpRefs>}
 */
class BloopersPlp extends Component {
  requiredRefs = ['results', 'status', 'grid', 'pagination', 'checkmarkSvg', 'notifyMeButton'];

  /** @type {HTMLFormElement | null} */
  #searchForm = null;

  /** @type {HTMLInputElement | null} */
  #initialsInput = null;

  /** @type {HTMLButtonElement | null} */
  #submitButton = null;

  /** @type {HTMLElement | null} */
  #monogramPreview = null;

  /** @type {HTMLElement | null} */
  #monogramPreviewText = null;

  /** @type {HTMLElement | null} */
  #nativeResults = null;

  /** @type {HTMLElement | null} */
  #productCountEl = null;

  /** @type {string | null} */
  #originalProductCountText = null;

  /** @type {HTMLElement | null} */
  #categoryFilter = null;

  /** @type {BlooperProduct[] | null} */
  #lastProducts = null;

  /**
   * Matches the 750px breakpoint used by the theme's facets CSS.
   * @type {MediaQueryList}
   */
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {IntersectionObserver | null} */
  #notifyMeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #notifyMeLoadingTimeout = null;

  /** @type {'initials' | 'category' | null} */
  #activeMode = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#bindHeaderSearchForm()) return;

    const initials = this.#getInitialsFromUrl();
    if (initials) {
      this.#initialsInput.value = initials;
      this.#setLoading(true);
    }
    this.#syncMonogramPreview(this.#initialsInput.value);

    const collectionWrapper = this.closest('.collection-wrapper');
    this.#nativeResults = this.closest('results-list')?.querySelector('#ResultsList') ?? null;
    this.#productCountEl = collectionWrapper?.querySelector('.facets-controls-wrapper .products-count-wrapper span') ?? null;
    this.#originalProductCountText = this.#productCountEl?.textContent ?? null;

    this.refs.notifyMeButton.addEventListener('click', this.#onNotifyMe);
    this.#observeNotifyMeVisibility();
    this.#bindCategoryFilterPills();
    window.addEventListener('popstate', this.#onPopState);

    this.#syncCategoryFilterPills();
    this.#runActiveSearch(this.#getPageFromUrl());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#searchForm?.removeEventListener('submit', this.#onSubmit);
    this.#searchForm?.removeEventListener('input', this.#onInput);
    this.#clearSubmitLoading();
    this.#searchForm = null;
    this.#initialsInput = null;
    this.#submitButton = null;
    this.#monogramPreview = null;
    this.#monogramPreviewText = null;
    this.refs.notifyMeButton.removeEventListener('click', this.#onNotifyMe);
    this.#notifyMeObserver?.disconnect();
    this.#notifyMeObserver = null;
    this.#clearNotifyMeLoading();
    this.#fetchController?.abort();
    this.#unbindCategoryFilterPills();
    this.#removeCategoryFilter();
    window.removeEventListener('popstate', this.#onPopState);
    this.#deactivateResults();
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  get #pageSize() {
    const parsed = Number(this.dataset.productsPerPage);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PAGE_SIZE;
  }

  get #isDesktop() {
    return this.#desktopMediaQuery.matches;
  }

  get #attentiveSignupUnitId() {
    const desktopId = this.dataset.attentiveDesktopSignupUnitId?.trim();
    const mobileId = this.dataset.attentiveMobileSignupUnitId?.trim();

    return this.#isDesktop ? desktopId || mobileId : mobileId || desktopId;
  }

  /** @returns {HTMLElement | null} */
  #getDrawerFiltersWrapper() {
    return document.querySelector(DRAWER_FILTERS_WRAPPER_SELECTOR);
  }

  /** @returns {boolean} */
  #bindHeaderSearchForm() {
    const form = document.querySelector(HEADER_SEARCH_FORM_SELECTOR);
    if (!(form instanceof HTMLFormElement)) return false;

    const initialsInput = form.querySelector('[name="initials"]');
    const submitButton = form.querySelector('[type="submit"]');
    const monogramPreview = form.querySelector('[data-bloopers-plp-preview]');
    const monogramPreviewText = form.querySelector('[data-bloopers-plp-preview-text]');
    if (!(initialsInput instanceof HTMLInputElement) || !(submitButton instanceof HTMLButtonElement)) {
      return false;
    }

    this.#searchForm = form;
    this.#initialsInput = initialsInput;
    this.#submitButton = submitButton;
    this.#monogramPreview = monogramPreview instanceof HTMLElement ? monogramPreview : null;
    this.#monogramPreviewText = monogramPreviewText instanceof HTMLElement ? monogramPreviewText : null;
    form.addEventListener('submit', this.#onSubmit);
    form.addEventListener('input', this.#onInput);

    return true;
  }

  #bindCategoryFilterPills() {
    const nav = document.querySelector(CATEGORY_FILTER_NAV_SELECTOR);
    nav?.addEventListener('click', this.#onCategoryPillClick);
  }

  #unbindCategoryFilterPills() {
    const nav = document.querySelector(CATEGORY_FILTER_NAV_SELECTOR);
    nav?.removeEventListener('click', this.#onCategoryPillClick);
  }

  /**
   * Intercepts category pill clicks so we fetch from the backend API instead of
   * relying on Shopify storefront filters (disabled above 5,000 products).
   * @param {MouseEvent} event
   */
  #onCategoryPillClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-blooper-filter]');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    const filterKey = (link.dataset.blooperFilter || '').trim().toLowerCase();
    const initials = this.#getInitialsFromUrl();
    const url = new URL(window.location.href);
    url.searchParams.delete(URL_PARAMS.category);
    url.searchParams.delete(URL_PARAMS.page);

    if (filterKey) {
      url.searchParams.set(URL_PARAMS.filter, filterKey);
    } else {
      url.searchParams.delete(URL_PARAMS.filter);
    }

    if (initials) {
      url.searchParams.set(URL_PARAMS.initials, initials);
    } else {
      url.searchParams.delete(URL_PARAMS.initials);
    }

    history.pushState({}, '', url.toString());
    this.#syncCategoryFilterPills();
    this.#runActiveSearch(1);
  };

  #onPopState = () => {
    const initials = this.#getInitialsFromUrl();
    if (this.#initialsInput) {
      this.#initialsInput.value = initials;
      this.#syncMonogramPreview(initials);
    }
    this.#syncCategoryFilterPills();
    this.#runActiveSearch(this.#getPageFromUrl());
  };

  #onInput = () => {
    if (!this.#initialsInput) return;
    const initials = this.#formatMonogramInitials(this.#initialsInput.value);
    this.#initialsInput.value = initials;
    this.#syncMonogramPreview(initials);
  };

  /**
   * Opens the Attentive sign-up unit. Called from the Notify Me button.
   *
   * @see https://help.attentivemobile.com/hc/en-us/articles/10913611055636-Set-up-Attentive-s-sign-up-unit-trigger
   */
  #onNotifyMe = () => {
    const signupUnitId = this.#attentiveSignupUnitId;
    if (!signupUnitId) return;

    const numericSignupUnitId = Number(signupUnitId);
    const creativeId = Number.isNaN(numericSignupUnitId) ? signupUnitId : numericSignupUnitId;
    const fire = () => {
      try {
        window.__attentive?.trigger?.(null, null, null, creativeId);
      } catch (error) {
        console.error('Attentive sign-up trigger failed:', error);
        this.#clearNotifyMeLoading();
      }
    };

    this.#setNotifyMeLoading(true);
    if (this.#notifyMeLoadingTimeout) clearTimeout(this.#notifyMeLoadingTimeout);
    this.#notifyMeLoadingTimeout = setTimeout(() => {
      this.#clearNotifyMeLoading();
    }, 2000);

    if (window.__attentive?.trigger) {
      fire();
    } else {
      setTimeout(fire, 500);
    }
  };

  /**
   * Reveals the notify-me button only after the user scrolls past the global back-to-top
   * threshold, so the two floating CTAs appear together.
   */
  #observeNotifyMeVisibility() {
    const sentinel = document.getElementById('backToTopSentinel');
    const { notifyMeButton } = this.refs;

    const setVisible = (isVisible) => {
      notifyMeButton.classList.toggle('is-visible', isVisible);
      notifyMeButton.tabIndex = isVisible ? 0 : -1;
    };

    if (!sentinel) {
      setVisible(true);
      return;
    }

    this.#notifyMeObserver = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    this.#notifyMeObserver.observe(sentinel);

    setVisible(window.scrollY > 300);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();

    const initials = this.#formatMonogramInitials(this.#initialsInput?.value || '');
    const filterKey = this.#getFilterKeyFromUrl();

    if (!initials) {
      // Clearing initials: keep the category filter if one is active.
      const url = new URL(window.location.href);
      url.searchParams.delete(URL_PARAMS.initials);
      url.searchParams.delete(URL_PARAMS.page);
      history.replaceState({}, '', url.toString());
      this.#syncMonogramPreview('');
      this.#runActiveSearch(1);
      return;
    }

    if (this.#initialsInput) {
      this.#initialsInput.value = initials;
    }
    this.#syncMonogramPreview(initials);

    const url = new URL(window.location.href);
    url.searchParams.set(URL_PARAMS.initials, initials);
    url.searchParams.delete(URL_PARAMS.category);
    url.searchParams.delete(URL_PARAMS.page);
    if (filterKey) {
      url.searchParams.set(URL_PARAMS.filter, filterKey);
    }
    history.replaceState({}, '', url.toString());

    this.#setLoading(true);
    this.#runActiveSearch(1);
  };

  /** @returns {string} */
  #getInitialsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return this.#formatMonogramInitials(params.get(URL_PARAMS.initials) || '');
  }

  /** @returns {string} */
  #getFilterKeyFromUrl() {
    const param = new URLSearchParams(window.location.search).get(URL_PARAMS.filter);
    return param?.trim().toLowerCase() || '';
  }

  /** @returns {number} */
  #getPageFromUrl() {
    const raw = Number(new URLSearchParams(window.location.search).get(URL_PARAMS.page));
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
  }

  /**
   * Formats text as the Bloopers monogram search value, e.g. `ACB`.
   * @param {unknown} value
   * @returns {string}
   */
  #formatMonogramInitials(value) {
    return Array.from(String(value || '').replace(/[^a-z]/gi, ''))
      .slice(0, 3)
      .map((character) => character.toUpperCase())
      .join('');
  }

  /** @param {string} value */
  #syncMonogramPreview(value) {
    if (!this.#monogramPreview || !this.#monogramPreviewText) return;

    const initials = this.#formatMonogramInitials(value);
    const placeholder = this.#formatMonogramInitials(this.#monogramPreview.dataset.bloopersPlpPreviewPlaceholder || '');
    this.#monogramPreview.toggleAttribute('data-empty', !initials);
    this.#renderMonogramPreview(this.#monogramPreviewText, initials || placeholder);
  }

  /**
   * Renders the Bloopers monogram display order. Initials are typed in name
   * order (first, middle, last), but the monogram displays the last initial
   * larger in the center: first, last (center), middle. Single- and
   * two-letter initials stay equal-sized, per the monogram guide.
   * @param {HTMLElement} preview
   * @param {string} value
   */
  #renderMonogramPreview(preview, value) {
    const [first = '', second = '', third = ''] = Array.from(this.#formatMonogramInitials(value));
    preview.replaceChildren();

    const isThreeLetter = Boolean(first && second && third);

    const orderedCharacters = [
      { value: first, isCenter: false },
      { value: third, isCenter: isThreeLetter },
      { value: second, isCenter: false },
    ].filter(({ value: character }) => character);

    for (const { value: character, isCenter } of orderedCharacters) {
      const characterNode = document.createElement('span');
      characterNode.className = 'bloopers-plp__preview-character';
      if (isCenter) {
        characterNode.classList.add('bloopers-plp__preview-character--center');
      }
      characterNode.textContent = character;
      preview.append(characterNode);
    }
  }

  /** @returns {string[]} */
  #getCategoriesFromUrl() {
    const param = new URLSearchParams(window.location.search).get(URL_PARAMS.category);
    if (!param) return [];
    return param.split(',').filter(Boolean);
  }

  /** @param {string[]} categories */
  #updateCategoryParam(categories) {
    const url = new URL(window.location.href);

    if (categories.length > 0) {
      url.searchParams.set(URL_PARAMS.category, categories.join(','));
    } else {
      url.searchParams.delete(URL_PARAMS.category);
    }

    history.replaceState({}, '', url.toString());
  }

  /** @param {string} text */
  #setStatus(text) {
    const { status } = this.refs;
    status.textContent = text;
    status.hidden = !text;
  }

  /** @returns {HTMLButtonElement | null} */
  #getHeaderSubmitButton() {
    if (this.#submitButton instanceof HTMLButtonElement) return this.#submitButton;

    const form = document.querySelector(HEADER_SEARCH_FORM_SELECTOR);
    const submitButton = form?.querySelector('[type="submit"]');
    return submitButton instanceof HTMLButtonElement ? submitButton : null;
  }

  #clearSubmitLoading() {
    const submitButton = this.#getHeaderSubmitButton();
    if (!submitButton) return;

    submitButton.classList.remove('bloopers-plp__submit--loading');
    submitButton.disabled = false;
    submitButton.removeAttribute('aria-busy');
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const submitButton = this.#getHeaderSubmitButton();
    if (!submitButton) return;

    submitButton.classList.toggle('bloopers-plp__submit--loading', loading);
    submitButton.disabled = loading;
    submitButton.toggleAttribute('aria-busy', loading);
  }

  /** @param {boolean} loading */
  #setNotifyMeLoading(loading) {
    const { notifyMeButton } = this.refs;
    notifyMeButton.classList.toggle('bloopers-plp__notify-me--loading', loading);
    notifyMeButton.disabled = loading;
    notifyMeButton.toggleAttribute('aria-busy', loading);
  }

  #clearNotifyMeLoading() {
    if (this.#notifyMeLoadingTimeout) clearTimeout(this.#notifyMeLoadingTimeout);
    this.#notifyMeLoadingTimeout = null;
    this.#setNotifyMeLoading(false);
  }

  /**
   * @param {'initials' | 'category'} mode
   */
  #revealResults(mode) {
    if (this.#nativeResults) this.#nativeResults.style.display = 'none';
    this.refs.results.hidden = false;
    this.dataset.active = '';
    this.dataset.mode = mode;
    this.#activeMode = mode;
  }

  #showResults() {
    this.#setLoading(false);
  }

  /** Restores the default PLP when no initials/category mode is active. */
  #deactivateResults() {
    delete this.dataset.active;
    delete this.dataset.mode;
    this.#activeMode = null;
    this.#removeCategoryFilter();
    this.#lastProducts = null;
    this.#clearPagination();

    if (this.#nativeResults) this.#nativeResults.style.display = '';
    this.refs.results.hidden = true;
    this.refs.grid.replaceChildren();
    this.#setStatus('');
    this.#setLoading(false);
    if (this.#productCountEl && this.#originalProductCountText != null) {
      this.#productCountEl.textContent = this.#originalProductCountText;
    }
  }

  /**
   * @param {string} message
   */
  #showEmptyState(message) {
    this.#lastProducts = null;
    this.#removeCategoryFilter();
    this.#clearPagination();
    this.refs.grid.replaceChildren();
    this.#showResults();
    this.#setStatus(message);
  }

  /**
   * Decides between native PLP, category mode, initials mode, or combined mode
   * based on the current URL params, then fetches from the app-proxy endpoint.
   * @param {number} [page]
   */
  #runActiveSearch(page = 1) {
    const initials = this.#getInitialsFromUrl();
    const filterKey = this.#getFilterKeyFromUrl();

    if (!initials && !filterKey) {
      this.#deactivateResults();
      return;
    }

    const mode = initials ? 'initials' : 'category';
    this.#revealResults(mode);
    this.#lastProducts = null;
    this.#removeCategoryFilter();
    this.#clearPagination();
    this.refs.grid.replaceChildren();
    this.#setStatus(Theme.translations?.bloopers_loading || 'Loading bloopers...');
    this.#fetchAppResults({ initials, filterKey, page });
  }

  /**
   * Maps a pill key (e.g. "clothing") to the backend category value ("BLOOPER CLOTHING").
   * @param {string} filterKey
   * @returns {string}
   */
  #toApiCategory(filterKey) {
    const key = filterKey.trim();
    if (!key) return '';
    if (/^blooper\s+/i.test(key)) return key.toUpperCase();
    return `BLOOPER ${key.toUpperCase()}`;
  }

  /**
   * @param {{ initials?: string, filterKey?: string, page?: number }} options
   */
  async #fetchAppResults({ initials = '', filterKey = '', page = 1 }) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const hasInitials = Boolean(initials);
    const apiCategory = filterKey ? this.#toApiCategory(filterKey) : '';
    const usePagination = Boolean(apiCategory) || hasInitials;

    try {
      const url = new URL(SEARCH_ENDPOINT, window.location.origin);

      if (hasInitials) {
        url.searchParams.set(URL_PARAMS.initials, initials);
      }
      if (apiCategory) {
        url.searchParams.set('category', apiCategory);
      }

      // Category (and combined) results use endpoint pagination. Initials-only
      // also paginates when the endpoint returns pagination metadata.
      url.searchParams.set('page', String(page));
      url.searchParams.set('limit', String(hasInitials && !apiCategory ? 50 : this.#pageSize));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        signal: this.#fetchController.signal,
      });

      if (!response.ok) {
        throw new Error(`Bloopers search failed with status ${response.status}`);
      }

      const data = await response.json();
      const products = data?.blooperProducts;
      /** @type {BlooperPagination} */
      const pagination = data?.pagination || {};
      const total = typeof data?.total === 'number' ? data.total : products?.length || 0;

      if (!Array.isArray(products)) {
        throw new Error('Bloopers search response missing blooperProducts array');
      }

      if (products.length === 0) {
        const emptyMessage = hasInitials
          ? Theme.translations?.bloopers_empty || 'No bloopers found for these initials.'
          : Theme.translations?.bloopers_empty_category || 'No bloopers found for this category.';
        this.#showEmptyState(emptyMessage);
        this.#updateProductCount(0);
        return;
      }

      this.#lastProducts = products;
      this.#setStatus('');

      // Drawer multi-select is only for initials-only results (no active pill).
      const buildDrawerFilter = hasInitials && !apiCategory;
      this.#renderProducts(products, { buildDrawerFilter });

      if (usePagination && !buildDrawerFilter) {
        this.#renderPagination({
          page: pagination.page || page,
          totalPages: pagination.totalPages || 1,
          filterKey,
          initials,
        });
      } else if (usePagination && pagination.totalPages > 1) {
        this.#renderPagination({
          page: pagination.page || page,
          totalPages: pagination.totalPages || 1,
          filterKey,
          initials,
        });
      } else {
        this.#clearPagination();
      }

      this.#updateProductCount(total);
      this.#showResults();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('Bloopers search failed:', error);
      this.#showEmptyState(Theme.translations?.bloopers_error || 'We could not load bloopers right now. Please try again.');
      this.#updateProductCount(0);
    }
  }

  /**
   * @param {BlooperProduct[]} products
   * @param {{ buildDrawerFilter?: boolean }} [options]
   */
  #renderProducts(products, options = {}) {
    const cards = products.map((product) => this.#createProductCard(product));
    this.refs.grid.replaceChildren(...cards);

    if (options.buildDrawerFilter) {
      this.#buildCategoryFilter(products);
      this.#applyFilter();
    } else {
      this.#removeCategoryFilter();
    }
  }

  /**
   * @param {{ page: number, totalPages: number, filterKey?: string, initials?: string }} options
   */
  #renderPagination({ page, totalPages, filterKey = '', initials = '' }) {
    const { pagination } = this.refs;
    pagination.replaceChildren();

    if (totalPages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;

    const list = document.createElement('ul');
    list.className = 'pagination__list';
    list.setAttribute('role', 'list');

    list.append(this.#createPaginationArrow('previous', page > 1 ? page - 1 : null, filterKey, initials));

    const maxButtons = 7;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    if (start > 1) {
      list.append(this.#createPaginationPage(1, page, filterKey, initials));
      if (start > 2) list.append(this.#createPaginationEllipsis());
    }

    for (let pageNumber = start; pageNumber <= end; pageNumber++) {
      list.append(this.#createPaginationPage(pageNumber, page, filterKey, initials));
    }

    if (end < totalPages) {
      if (end < totalPages - 1) list.append(this.#createPaginationEllipsis());
      list.append(this.#createPaginationPage(totalPages, page, filterKey, initials));
    }

    list.append(this.#createPaginationArrow('next', page < totalPages ? page + 1 : null, filterKey, initials));
    pagination.append(list);
  }

  #clearPagination() {
    const { pagination } = this.refs;
    pagination.replaceChildren();
    pagination.hidden = true;
  }

  /**
   * @param {'previous' | 'next'} direction
   * @param {number | null} targetPage
   * @param {string} filterKey
   * @param {string} initials
   * @returns {HTMLLIElement}
   */
  #createPaginationArrow(direction, targetPage, filterKey, initials) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    const label =
      direction === 'previous'
        ? Theme.translations?.pagination_previous || 'Previous'
        : Theme.translations?.pagination_next || 'Next';

    if (targetPage == null) {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--arrow pagination__link--disabled';
      span.setAttribute('aria-label', label);
      span.setAttribute('aria-disabled', 'true');
      span.textContent = direction === 'previous' ? '‹' : '›';
      li.append(span);
      return li;
    }

    const link = document.createElement('a');
    link.className = 'pagination__link pagination__link--arrow';
    link.href = this.#buildResultsPageHref(filterKey, initials, targetPage);
    link.setAttribute('aria-label', label);
    link.textContent = direction === 'previous' ? '‹' : '›';
    link.addEventListener('click', (event) => this.#onPaginationClick(event, targetPage));
    li.append(link);
    return li;
  }

  /**
   * @param {number} pageNumber
   * @param {number} currentPage
   * @param {string} filterKey
   * @param {string} initials
   * @returns {HTMLLIElement}
   */
  #createPaginationPage(pageNumber, currentPage, filterKey, initials) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    if (pageNumber === currentPage) {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--current';
      span.setAttribute('aria-current', 'page');
      span.textContent = String(pageNumber);
      li.append(span);
      return li;
    }

    const link = document.createElement('a');
    link.className = 'pagination__link';
    link.href = this.#buildResultsPageHref(filterKey, initials, pageNumber);
    link.textContent = String(pageNumber);
    link.addEventListener('click', (event) => this.#onPaginationClick(event, pageNumber));
    li.append(link);
    return li;
  }

  /** @returns {HTMLLIElement} */
  #createPaginationEllipsis() {
    const li = document.createElement('li');
    li.className = 'pagination__item';
    const span = document.createElement('span');
    span.className = 'pagination__link pagination__link--disabled';
    span.textContent = '…';
    span.setAttribute('aria-hidden', 'true');
    li.append(span);
    return li;
  }

  /**
   * @param {string} filterKey
   * @param {string} initials
   * @param {number} page
   * @returns {string}
   */
  #buildResultsPageHref(filterKey, initials, page) {
    const url = new URL(window.location.pathname, window.location.origin);
    if (filterKey) url.searchParams.set(URL_PARAMS.filter, filterKey);
    if (initials) url.searchParams.set(URL_PARAMS.initials, initials);
    if (page > 1) url.searchParams.set(URL_PARAMS.page, String(page));
    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {MouseEvent} event
   * @param {number} page
   */
  #onPaginationClick = (event, page) => {
    event.preventDefault();

    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set(URL_PARAMS.page, String(page));
    } else {
      url.searchParams.delete(URL_PARAMS.page);
    }
    history.pushState({}, '', url.toString());

    this.#runActiveSearch(page);
    this.refs.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * @param {BlooperProduct} product
   * @returns {HTMLLIElement}
   */
  #createProductCard(product) {
    const title = product.title || '';
    const href = this.#getProductHref(product);
    const monogram = this.#formatMonogramInitials(product.blooperValue || '');
    const size = product.size || '';

    const item = document.createElement('li');
    item.dataset.category = this.#extractCategory(product);

    const media = this.#createMedia(product.imageUrl || '', title, size);

    const detailsTop = document.createElement('div');
    detailsTop.className = 'bloopers-plp-card__details-top';
    detailsTop.append(this.#createTextElement('span', 'bloopers-plp-card__price', this.#formatPrice(product.price)));
    if (monogram) {
      detailsTop.append(this.#createTextElement('span', 'bloopers-plp-card__monogram', monogram));
    }

    const titleEl = this.#createTextElement('p', 'bloopers-plp-card__title', title);

    const content = document.createElement('div');
    content.className = 'bloopers-plp-card';
    content.append(media, detailsTop, titleEl);

    if (href) {
      const link = document.createElement('a');
      link.className = 'bloopers-plp-card__link';
      link.href = href;
      link.append(content);
      item.append(link);
    } else {
      item.append(content);
    }

    return item;
  }

  /**
   * @param {string} imageUrl
   * @param {string} alt
   * @param {string} size
   * @returns {HTMLElement}
   */
  #createMedia(imageUrl, alt, size) {
    const wrapper = document.createElement('div');
    wrapper.className = 'bloopers-plp-card__media';

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'bloopers-plp-card__image';
      image.src = imageUrl;
      image.alt = alt;
      image.loading = 'lazy';
      wrapper.append(image);
    }

    if (size) {
      const badge = document.createElement('span');
      badge.className = 'bloopers-plp-card__size-badge';
      badge.textContent = size;
      wrapper.append(badge);
    }

    return wrapper;
  }

  /**
   * @param {string} tagName
   * @param {string} className
   * @param {string} text
   * @returns {HTMLElement}
   */
  #createTextElement(tagName, className, text) {
    const element = document.createElement(tagName);
    element.className = className;
    element.textContent = text || '';
    return element;
  }

  /**
   * @param {string | number | undefined} price
   * @returns {string}
   */
  #formatPrice(price) {
    if (price == null || price === '') return '';

    const priceString = String(price);
    const minorUnits = convertMoneyToMinorUnits(priceString, this.#currency);
    if (minorUnits === null) return priceString;

    return formatMoney(minorUnits, this.#moneyFormat, this.#currency);
  }

  /**
   * @param {BlooperProduct} product
   * @returns {string}
   */
  #getProductHref(product) {
    if (!product.handle) return '';

    const variantQuery = product.variantId ? `?variant=${product.variantId}` : '';
    return `/products/${product.handle}${variantQuery}`;
  }

  /** Syncs filled/outline pill styles from the current `?filter=` URL param. */
  #syncCategoryFilterPills() {
    const activeFilter = this.#getFilterKeyFromUrl();
    const nav = document.querySelector(CATEGORY_FILTER_NAV_SELECTOR);
    if (!nav) return;

    for (const link of nav.querySelectorAll('a.button-1')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      let filterKey = '';
      if (link.hasAttribute('data-blooper-filter')) {
        filterKey = (link.dataset.blooperFilter || '').toLowerCase();
      } else {
        try {
          const url = new URL(link.href, window.location.origin);
          filterKey = (url.searchParams.get(URL_PARAMS.filter) || '').trim().toLowerCase();
        } catch {
          filterKey = '';
        }
      }

      const isActive = filterKey === activeFilter;
      link.classList.toggle('is-active', isActive);
      link.classList.toggle('button-inverted', !isActive);
      link.toggleAttribute('aria-current', isActive);
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #formatCategoryLabel(value) {
    const trimmed = value.trim();
    if (!trimmed) return '';

    return trimmed.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #stripBlooperPrefix(value) {
    const trimmed = value.trim();
    if (!trimmed) return '';

    const match = trimmed.match(/^blooper\s+(.+)$/i);
    return match?.[1]?.trim() || trimmed;
  }

  /**
   * @param {BlooperProduct} product
   * @returns {string}
   */
  #extractCategory(product) {
    const blooperType = product.blooperType?.trim();
    if (blooperType) return this.#formatCategoryLabel(this.#stripBlooperPrefix(blooperType));

    const productType = product.type?.trim();
    if (productType) return this.#formatCategoryLabel(this.#stripBlooperPrefix(productType));

    const match = (product.title || '').match(/^BLOOPER\s+(.+?):/i);
    const captured = match?.[1]?.trim();
    if (!captured) return '';

    return this.#formatCategoryLabel(captured);
  }

  /**
   * @param {BlooperProduct[]} products
   * @returns {Map<string, number>}
   */
  #countCategories(products) {
    const counts = new Map();
    for (const product of products) {
      const category = this.#extractCategory(product);
      if (category) counts.set(category, (counts.get(category) || 0) + 1);
    }
    return counts;
  }

  /**
   * Injects a flat category checklist into the filter drawer when initials search is active.
   * @param {BlooperProduct[]} products
   */
  #buildCategoryFilter(products) {
    this.#removeCategoryFilter();

    const counts = this.#countCategories(products);
    if (counts.size === 0) return;

    const target = this.#getDrawerFiltersWrapper();
    if (!target) return;

    const selectedCategories = this.#getCategoriesFromUrl();

    const filterItem = document.createElement('div');
    filterItem.className = 'facets__item bloopers-plp-category-filter';

    const label = document.createElement('p');
    label.className = 'facets__label';
    label.textContent = Theme.translations?.bloopers_category || 'Category';

    const list = document.createElement('ul');
    list.className = 'facets__inputs-list bloopers-plp-filter__list';
    list.setAttribute('role', 'list');

    const sorted = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [category, count] of sorted) {
      list.append(this.#createCategoryOption(category, count, selectedCategories.includes(category)));
    }

    filterItem.append(label, list);
    filterItem.addEventListener('change', this.#onCategoryChange);
    target.prepend(filterItem);
    this.#categoryFilter = filterItem;

    this.#updateCategoryParam(this.#getCheckedCategories());
  }

  /**
   * @param {string} category
   * @param {number} count
   * @param {boolean} isSelected
   * @returns {HTMLLIElement}
   */
  #createCategoryOption(category, count, isSelected) {
    const id = `bloopers-filter-${category.toLowerCase().replace(/\s+/g, '-')}`;

    const li = document.createElement('li');
    li.className = 'facets__inputs-list-item';

    const wrapper = document.createElement('div');
    wrapper.className = 'checkbox';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'checkbox__input';
    input.name = CATEGORY_INPUT_NAME;
    input.value = category;
    input.id = id;
    input.checked = isSelected;

    const labelEl = document.createElement('label');
    labelEl.className = 'checkbox__label';
    labelEl.setAttribute('for', id);
    labelEl.setAttribute('role', 'checkbox');
    labelEl.append(this.refs.checkmarkSvg.content.cloneNode(true));
    labelEl.append(this.#createTextElement('span', 'checkbox__label-text', `${category} (${count})`));

    wrapper.append(input, labelEl);
    li.append(wrapper);
    return li;
  }

  #removeCategoryFilter() {
    if (this.#categoryFilter) {
      this.#categoryFilter.removeEventListener('change', this.#onCategoryChange);
      this.#categoryFilter.remove();
      this.#categoryFilter = null;
    }
  }

  #onCategoryChange = () => {
    const checked = this.#getCheckedCategories();
    this.#updateCategoryParam(checked);
    this.#applyFilter();
  };

  /** @returns {string[]} */
  #getCheckedCategories() {
    if (!this.#categoryFilter) return [];

    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.#categoryFilter.querySelectorAll(`input[name="${CATEGORY_INPUT_NAME}"]:checked`)
    );
    return [...inputs].map((input) => input.value);
  }

  #applyFilter() {
    const { grid } = this.refs;

    const selected = this.#getCheckedCategories();
    const showAll = selected.length === 0;
    let visibleCount = 0;

    for (const item of grid.children) {
      if (!(item instanceof HTMLElement)) continue;
      const visible = showAll || selected.includes(item.dataset.category || '');
      item.hidden = !visible;
      if (visible) visibleCount++;
    }

    this.#updateProductCount(visibleCount);
  }

  /** @param {number} count */
  #updateProductCount(count) {
    if (!this.#productCountEl) return;
    this.#productCountEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
  }
}

if (!customElements.get('bloopers-plp')) {
  customElements.define('bloopers-plp', BloopersPlp);
}
